import cn from "classnames";
import { useRef } from "react";
import { Text } from "../text";
import { SIZES } from "./constants";
import { RadioProps } from "./types";
import styles from "./styles.module.scss";

export const Radio = ({
  id,
  name,
  size = SIZES.LARGE,
  label,
  value,
  checked,
  disabled,
  onChange,
  className,
  dataTestId = "radio",
}: RadioProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // При отсутствии свойства `onChange` radio-кнопка является презентационной, то есть просто отображает переданное свойство `checked`.
  // Термин презентационный не совсем корректный с точки зрения его фактического определения, мы назвали его так потому что не придумали ничего лучше.
  // Презентационная radio-кнопка не будет содержать input c его нативным поведением как элемента формы (focus по клику, tab навигация).
  // Подробный кейс использования презентационного режима и какую проблему он решает можно найти в компоненте Checkbox. На данный момент у нас нет подобного кейса для Radio, мы добавили эту логику в целях унификации кодовой базы основных контролов проекта (Checkbox, Radio, Switch).
  const isPresentationalMode = !onChange;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (inputRef.current) {
      onChange?.(event, {
        value: inputRef.current.value,
        checked: inputRef.current.checked,
      });
    }
  };

  const handleMouseDownOnLabel = (
    event: React.MouseEvent<HTMLLabelElement>
  ) => {
    if (isPresentationalMode) {
      // В презентационном режиме работы предотвращаем передачу фокуса на label
      event.preventDefault();
    }
  };

  return (
    <label
      htmlFor={id}
      data-test-id={dataTestId}
      onMouseDown={handleMouseDownOnLabel}
      className={cn(className, styles.radio, {
        [styles.checked]: checked,
        [styles.disabled]: disabled,
      })}
    >
      <span
        data-test-id={`${dataTestId}-container`}
        className={cn(styles.checkmark, styles[size])}
      >
        {checked && <span className={styles.dot} />}
        {!isPresentationalMode && (
          <input
            id={id}
            name={name}
            type="radio"
            value={value}
            ref={inputRef}
            checked={checked}
            disabled={disabled}
            onChange={handleChange}
            className={styles.input}
            data-test-id={`${dataTestId}-input`}
          />
        )}
      </span>
      {label && (
        <Text
          className={cn(styles.label, styles[size])}
          dataTestId={`${dataTestId}-label`}
          size={size}
        >
          {label}
        </Text>
      )}
    </label>
  );
};
